import { compressImageForDatabase, ensureImageSizeWithinLimit } from '@/utils/imageUpload';
import { fabric } from 'fabric';
import { renderFabricJsonToDataUrl } from '@/pages/ipe/workbench/utils/fabricCanvasComposite';
import { refreshAllStampLayerVisuals } from './stampLayerUtils';
import { isSelectionOverlayObject } from './selectionUtils';
import { getWorkspaceSizeFromFabricJson } from './slideModel';

export const CANVAS_PREVIEW_IMAGE_SUFFIX = '__canvas_preview__';

export function buildCanvasPreviewImageId(slideId) {
  if (!slideId) return null;
  return `${slideId}${CANVAS_PREVIEW_IMAGE_SUFFIX}`;
}

export function isCanvasPreviewImageId(imageId) {
  return typeof imageId === 'string' && imageId.endsWith(CANVAS_PREVIEW_IMAGE_SUFFIX);
}

function runEditorHook(editor, hookName) {
  const hook = editor?.hooksEntity?.[hookName];
  if (!hook || typeof hook.callAsync !== 'function') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    hook.callAsync('', () => resolve());
  });
}

function getWorkspaceSaveOption(canvas) {
  const workspace = canvas.getObjects().find((item) => item.id === 'workspace');
  if (!workspace) {
    return {
      format: 'png',
      quality: 1,
      width: canvas.width,
      height: canvas.height,
      left: 0,
      top: 0,
    };
  }
  return {
    format: 'png',
    quality: 1,
    width: workspace.width,
    height: workspace.height,
    left: workspace.left || 0,
    top: workspace.top || 0,
  };
}

function prepareFabricJsonForPreviewExport(fabricJson) {
  if (!fabricJson?.objects) return fabricJson;
  return {
    ...fabricJson,
    objects: fabricJson.objects.filter((obj) => {
      if (obj?.id === 'workspace') return true;
      if (isSelectionOverlayObject(obj)) return false;
      if (obj?.excludeFromExport) return false;
      return true;
    }),
  };
}

async function measureDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || null,
        height: img.naturalHeight || null,
      });
    };
    img.onerror = () => resolve({ width: null, height: null });
    img.src = dataUrl;
  });
}

async function compressPreviewDataUrl(dataUrl, width, height) {
  if (!dataUrl?.startsWith('data:image/')) return dataUrl;
  try {
    let compressed = await compressImageForDatabase(
      dataUrl,
      width || 2000,
      height || 2000,
    );
    compressed = await ensureImageSizeWithinLimit(
      compressed,
      width || 2000,
      height || 2000,
    );
    return compressed;
  } catch {
    return dataUrl;
  }
}

/**
 * 从当前编辑器画布导出 workspace 区域 PNG（与「导出 png」一致）
 */
export async function exportActiveEditorCanvasPreview(editor) {
  const canvas = editor?.canvas;
  if (!canvas) return null;

  const viewportTransform = canvas.viewportTransform?.slice?.();
  const hiddenOverlays = [];

  canvas.discardActiveObject?.();
  canvas.getObjects().forEach((obj) => {
    if (isSelectionOverlayObject(obj) && obj.visible !== false) {
      hiddenOverlays.push(obj);
      obj.visible = false;
    }
  });

  await runEditorHook(editor, 'hookSaveBefore');
  refreshAllStampLayerVisuals(canvas, fabric);
  canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  canvas.renderAll();

  try {
    const option = getWorkspaceSaveOption(canvas);
    const dataUrl = canvas.toDataURL(option);
    const { width, height } = await measureDataUrl(dataUrl);
    return compressPreviewDataUrl(dataUrl, width, height);
  } catch (error) {
    console.warn('导出画布预览失败:', error);
    return null;
  } finally {
    hiddenOverlays.forEach((obj) => {
      obj.visible = true;
    });
    if (viewportTransform) {
      canvas.setViewportTransform(viewportTransform);
    }
    canvas.renderAll();
    await runEditorHook(editor, 'hookSaveAfter');
  }
}

/**
 * 离屏渲染指定 slide 的 fabric JSON 为 PNG
 */
export async function exportSlideFabricPreview(fabricJson) {
  if (!fabricJson?.objects?.length) return null;
  try {
    const prepared = prepareFabricJsonForPreviewExport(fabricJson);
    const dataUrl = await renderFabricJsonToDataUrl(prepared, {
      format: 'png',
      quality: 1,
    });
    const { width, height } = getWorkspaceSizeFromFabricJson(prepared);
    return compressPreviewDataUrl(dataUrl, width, height);
  } catch (error) {
    console.warn('离屏导出画布预览失败:', error);
    return null;
  }
}

/**
 * 保存模板时生成列表预览图：优先当前编辑页，否则离屏渲染首张 slide
 */
export async function exportDesignerTemplatePreview({
  editor,
  slides = [],
  previewSlideId,
  activeSlideId,
}) {
  const targetId = previewSlideId || slides[0]?.id;
  if (!targetId) return null;

  if (activeSlideId && targetId === activeSlideId && editor?.canvas) {
    const live = await exportActiveEditorCanvasPreview(editor);
    if (live) return live;
  }

  const slide = slides.find((s) => s.id === targetId) || slides[0];
  return exportSlideFabricPreview(slide?.fabricCanvas);
}
